import {
    CoreGridInput,
    CoreOrder,
    CoreProduct,
    CoreReturn,
    CoreReturnGrid,
    CoreReturnsGridOutput,
    fromCoreReturnNames,
    GridInput,
    GridOutput,
    Return,
    SkladonError,
    HTTP_STATUS,
    toCoreReturnNames
} from 'skladon-types';

import { CoreGridMapper } from './CoreGridMapper';

export class ReturnsMapper {
    public static fromCore(coreReturn: CoreReturn): Return {
        return {
            ...ReturnsMapper.fromCoreBase(coreReturn),
            eshopOrderId: coreReturn.ESHOP_ORDER_ID || null
        };
    }

    public static fromCoreReturnGrid(coreReturn: CoreReturnGrid): Return {
        return {
            ...ReturnsMapper.fromCoreBase(coreReturn),
            eshopOrderId: coreReturn.ESHOP_ORDER_ID,
            recipientName: coreReturn.RECIPIENT_NAME ?? null,
            orderShipper: coreReturn.ORDER_SHIPPER ?? null,
            orderShipperServiceCode: coreReturn.ORDER_SHIPPER_SERVICE_CODE ?? null,
            orderTrackingNumber: coreReturn.ORDER_TRACKING_NUMBER ?? null
        };
    }

    public static toCore(
        returns: Partial<Return>,
        coreProducts: CoreProduct[],
        coreOrder?: CoreOrder
    ): Partial<CoreReturn> {
        returns.files?.forEach((file) => {
            if (file.id && file.data) {
                throw new SkladonError(HTTP_STATUS.BAD_REQUEST, `You cannot send file with both id and data!`);
            }
        });
        // Helper to pass through null and empty string as-is for clearable fields
        const toClearableValue = (value: string | null | undefined): string | null | undefined => {
            if (value === undefined) return undefined;
            // Pass through null and empty string as-is
            return value;
        };

        return {
            DESCRIPTION: toClearableValue(returns.description) as string | undefined,
            ORDER_ID: coreOrder?.ID ?? undefined,
            ESHOP_RETURN_ID: returns.eshopReturnId,
            FILES: returns.files
                ? returns.files.map((file) => ({
                      id: file.id!,
                      type: file.type,
                      data: file.data,
                      fileName: file.fileName,
                      fileType: file.fileType,
                      fileSize: file.fileSize || 'unknown'
                  }))
                : undefined,
            NOTE: toClearableValue(returns.note) as string | undefined,
            ORDER_TYPE: returns.orderType,
            RETURNED_ORDER_PRODUCTS: returns.returnedOrderProducts
                ? returns.returnedOrderProducts.map((product) => {
                      const foundProduct = coreProducts.find((coreProduct) => coreProduct.SKU === product.sku);
                      return {
                          PRODUCT_ID: foundProduct!.ID,
                          SKU: product.sku,
                          NAME: product.sku,
                          RETURN_REASON: product.returnReason ?? undefined,
                          QUANTITY: product.quantity,
                          LINE_STATE: product.lineState
                      } as any; // Type assertion needed due to complex Core type requirements
                  })
                : undefined,
            SHIPPER: toClearableValue(returns.shipper) as string | undefined,
            SHIPPER_SERVICE_CODE: toClearableValue(returns.shipperServiceCode) as string | undefined,
            TRACKING_NUMBER: toClearableValue(returns.trackingNumber) as string | undefined,
            STATUS: returns.status ?? undefined,
            ESHOP_NOTE: toClearableValue(returns.eshopNote) as string | undefined
        };
    }

    public static fromCoreGrid(grid: CoreReturnsGridOutput): GridOutput {
        return {
            data: grid.data
                .map((coreReturn) => ReturnsMapper.fromCoreReturnGrid(coreReturn))
                .map((returns) => CoreGridMapper.omitColumns(returns, fromCoreReturnNames, grid.request.columns)),
            request: CoreGridMapper.mapGrid(grid.request, fromCoreReturnNames, 1000),
            totalCount: grid.totalCount
        };
    }

    public static toCoreGrid(grid: GridInput): CoreGridInput {
        return CoreGridMapper.mapGrid(grid, toCoreReturnNames, 1000);
    }

    private static fromCoreBase(coreReturn: CoreReturn | CoreReturnGrid) {
        const hasProducts = (value: CoreReturn | CoreReturnGrid): value is CoreReturn =>
            'RETURNED_ORDER_PRODUCTS' in value;

        const hasFiles = (value: CoreReturn | CoreReturnGrid): value is CoreReturn => 'FILES' in value;

        const hasEshopNote = (value: CoreReturn | CoreReturnGrid): value is CoreReturn => 'ESHOP_NOTE' in value;

        return {
            id: coreReturn.ID,
            description: coreReturn.DESCRIPTION ?? null,
            eshopReturnId: coreReturn.ESHOP_RETURN_ID,
            partnerOrderId: coreReturn.PARTNER_ORDER_ID ?? null,
            files: hasFiles(coreReturn) ? coreReturn.FILES : undefined,
            note: coreReturn.NOTE ?? null,
            orderType: coreReturn.ORDER_TYPE,
            returnedOrderProducts: hasProducts(coreReturn)
                ? coreReturn.RETURNED_ORDER_PRODUCTS.map(
                      (product): NonNullable<Return['returnedOrderProducts']>[number] => ({
                          sku: product.SKU,
                          resolution: product.RESOLUTION ?? null,
                          returnReason: product.RETURN_REASON,
                          quantity: product.QUANTITY,
                          received: product.RECEIVED,
                          lineState: product.LINE_STATE,
                          serialNumber: product.SERIAL_NUMBER || null
                      })
                  )
                : undefined,
            shipper: coreReturn.SHIPPER ?? null,
            shipperServiceCode: coreReturn.SHIPPER_SERVICE_CODE ?? null,
            status: coreReturn.STATUS,
            trackingNumber: coreReturn.TRACKING_NUMBER ?? null,
            rma: coreReturn.RMA,
            createdAt: String(coreReturn.CREATED_AT),
            canceledAt: coreReturn.CANCELED_AT ? String(coreReturn.CANCELED_AT) : null,
            confirmedAt: coreReturn.CONFIRMED_AT ? String(coreReturn.CONFIRMED_AT) : null,
            receivedAt: coreReturn.RECEIVED_AT ? String(coreReturn.RECEIVED_AT) : null,
            eshopNote: hasEshopNote(coreReturn) ? coreReturn.ESHOP_NOTE ?? null : null,
            sourceSys: coreReturn.SOURCE_SYS ?? null,
            transportPaidBy: coreReturn.TRANSPORT_PAID_BY ?? null,
            orderId: coreReturn.ORDER_ID ?? null,
            senderStreet: coreReturn.SENDER_STREET ?? null,
            senderCity: coreReturn.SENDER_CITY ?? null,
            senderState: coreReturn.SENDER_STATE ?? null,
            senderPostcode: coreReturn.SENDER_POSTCODE ?? null,
            senderPhone: coreReturn.SENDER_PHONE ?? null
        };
    }
}
